// client/src/lib/pushBridge.ts
//
// The push seam the SHARED client code calls (plan Task D6). On the web it is a no-op: nothing is
// registered, nothing is fetched. The store app (client-app/src/push.ts) injects the native
// FirebaseMessaging plugin through configurePushBridge() at boot — the web bundle never imports it.
//
//  - register(): only when the OS permission is ALREADY granted — never prompts.
//  - askPermission(): the ONLY place the OS prompt is shown (from the explanation sheet, after sign-in,
//    on the home screen — never at launch).
//  - unregister(): DELETE /api/me/push-token for this device. Sign-out order (Step 4):
//    unregister() → clearToken() → flushToken() → navigate.
//  - A tap routes in-app only: the route must be a known in-app path — never an absolute URL.
export type PushPermission = "granted" | "denied" | "prompt" | "unsupported";

/** The subset of @capacitor-firebase/messaging the bridge needs (adapted in client-app/src/push.ts). */
export interface PushPlugin {
  checkPermission(): Promise<PushPermission>;
  requestPermission(): Promise<PushPermission>;
  getToken(): Promise<string | null>;
  onTokenRefresh(cb: (token: string) => void): void;
  onTap(cb: (data: Record<string, unknown>) => void): void;
}

export interface PushBridgeDeps {
  plugin: PushPlugin;
  platform: "ios" | "android";
  deviceId: () => Promise<string>;
  /** Authenticated fetch to the API (the app's net seam: bearer token, app origin). */
  send: (method: "POST" | "DELETE", path: string, body: unknown) => Promise<{ ok: boolean; status: number }>;
  navigate: (route: string) => void;
}

export interface PushBridge {
  readonly supported: boolean;
  permission(): Promise<PushPermission>;
  register(): Promise<string | null>;
  askPermission(): Promise<PushPermission>;
  unregister(): Promise<void>;
}

/** In-app route prefixes a push may open. Anything else (incl. absolute / protocol-relative URLs) is ignored. */
export const PUSH_ROUTE_PREFIXES = [
  "/crew", "/v2", // crew app (D4) and portal v2
  // couple destinations for the D9 events (payment, invoice, MC, form, contract, live, photos)
  "/bookings", "/invoices", "/pay", "/mc-scheduling", "/event-form", "/event-day-form", "/live-stream", "/live-photos", "/notifications",
  "/admin", // manager v2
];

export function safePushRoute(route: unknown): string | null {
  if (typeof route !== "string" || !route.startsWith("/") || route.startsWith("//") || route.includes("\\")) return null;
  if (/^\/[^/]*:/.test(route)) return null;
  const path = route.split(/[?#]/)[0];
  return PUSH_ROUTE_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`)) ? route : null;
}

export const PUSH_TOKEN_PATH = "/api/me/push-token";

export function createPushBridge(deps: PushBridgeDeps | null): PushBridge {
  if (!deps) {
    return {
      supported: false,
      permission: async () => "unsupported",
      register: async () => null,
      askPermission: async () => "unsupported",
      unregister: async () => {},
    };
  }
  let lastToken: string | null = null;
  let registered = false;

  const post = async (token: string) => {
    const deviceId = await deps.deviceId();
    const r = await deps.send("POST", PUSH_TOKEN_PATH, { token, platform: deps.platform, deviceId });
    if (r.ok) { lastToken = token; registered = true; }
    return r.ok;
  };

  deps.plugin.onTokenRefresh((token) => {
    // Only after this device registered in this session (signed in + permission granted).
    if (!registered || !token || token === lastToken) return;
    void post(token).catch(() => {});
  });
  deps.plugin.onTap((data) => {
    const route = safePushRoute((data as any)?.route);
    if (route) deps.navigate(route);
  });

  const bridge: PushBridge = {
    supported: true,
    permission: () => deps.plugin.checkPermission().catch(() => "unsupported" as const),
    async register() {
      if ((await bridge.permission()) !== "granted") return null;
      const token = await deps.plugin.getToken().catch(() => null);
      if (!token) return null;
      return (await post(token).catch(() => false)) ? token : null;
    },
    async askPermission() {
      const p = await deps.plugin.requestPermission().catch(() => "denied" as const);
      if (p === "granted") await bridge.register();
      return p;
    },
    async unregister() {
      registered = false;
      lastToken = null;
      const deviceId = await deps.deviceId().catch(() => "");
      if (!deviceId) return;
      await deps.send("DELETE", PUSH_TOKEN_PATH, { deviceId }).catch(() => undefined);
    },
  };
  return bridge;
}

let current: PushBridge = createPushBridge(null);
export function configurePushBridge(b: PushBridge): void { current = b; }
export function pushBridge(): PushBridge { return current; }
export function resetPushBridge(): void { current = createPushBridge(null); }

/** One-line explanation shown before the OS prompt (Step 3), per audience. */
export const PUSH_ASK_COPY = {
  crew: "Get told when you're booked, moved or reminded",
  couple: "Get payment and event updates",
  manager: "Get updates for your events",
} as const;
export type PushAudience = keyof typeof PUSH_ASK_COPY;
